import React, { useState, useEffect } from 'react';
import './Wordgame.css';
import SEO from '../../SEO/SEO';
const wordsByDifficulty = {
  easy: [ 'home', 'dog', 'cat', 'book', 'play', 'food', 'home', 'car', 'ball', 'hat', 'sun', 'moon', 'tree', 'fish', 'door', 'hand', 'bird', 'shoe', 'baby', 'milk', 'sky', 'cake', 'smile', 'apple', 'chair', 'water', 'gift', 'light', 'love', 'sleep', 'star' ],
  medium: [ 'school', 'beach', 'music', 'forest', 'coffee', 'mountain', 'computer', 'garden', 'kitchen', 'travel', 'book', 'movie', 'ocean', 'space', 'winter', 'desert', 'city', 'river', 'jungle', 'island', 'castle', 'village', 'market', 'harbor', 'valley', 'canyon', 'glacier', 'volcano', 'rainforest', 'savannah' ],
  hard: [ 'tundra', 'swamp', 'meadow', 'cliff', 'lagoon', 'bay', 'reef', 'waterfall', 'cave', 'plateau', 'prairie', 'delta', 'fjord', 'gorge', 'hill', 'plain', 'ridge', 'spring', 'stream', 'grove', 'orchard', 'vineyard', 'field', 'pasture', 'moor', 'heath', 'steppe', 'dune', 'oasis', 'marsh' ],
  expert: [ 'archipelago', 'isthmus', 'peninsula', 'escarpment', 'butte', 'atoll', 'meridian', 'equator', 'latitude', 'longitude', 'permafrost', 'basin', 'mesa', 'estuary', 'aquifer', 'biome', 'ecosystem', 'ecotone', 'biodiversity', 'photosynthesis', 'symbiosis', 'adaptation', 'sustainability', 'conservation', 'desertification' ]
};

const getRandomWord = (difficulty) => {
  const words = wordsByDifficulty[difficulty];
  return words[Math.floor(Math.random() * words.length)];
};

const getDifficultyLevel = (score) => {
  if (score < 5) return 'easy';
  if (score < 10) return 'medium';
  if (score < 15) return 'hard';
  return 'expert';
};

const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const Wordgame = () => {
  const [currentDifficulty, setCurrentDifficulty] = useState('easy');
  const [currentWord, setCurrentWord] = useState(() => getRandomWord('easy'));
  const [options, setOptions] = useState([]);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [highestScore, setHighestScore] = useState(
    parseInt(localStorage.getItem('wordgameHighScore') || '0')
  );
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    generateOptions(currentWord);
    const savedHighScore = localStorage.getItem('wordgameHighScore');
    if (savedHighScore) {
      setHighestScore(parseInt(savedHighScore));
    }
  }, []);

  useEffect(() => {
    generateOptions(currentWord);
  }, [currentWord]);

  useEffect(() => {
    if (score > highestScore) {
      setHighestScore(score);
      localStorage.setItem('wordgameHighScore', score.toString());
    }
    setCurrentDifficulty(getDifficultyLevel(score));
  }, [score, highestScore]);

  const generateOptions = async (word) => {
    setLoading(true);
    try {
      const relatedResponse = await fetch(`https://api.datamuse.com/words?rel_trg=${word}&max=10`);
      const relatedData = await relatedResponse.json();
      const relatedWords = relatedData.map((item) => item.word);

      const unrelatedResponse = await fetch(`https://api.datamuse.com/words?rel_ant=${word}&max=10`);
      const unrelatedData = await unrelatedResponse.json();
      const unrelatedWords = unrelatedData.map((item) => item.word);

      let correctOption = relatedWords[0];
      if (!correctOption) {
        const fallbackResponse = await fetch(`https://api.datamuse.com/words?ml=${word}&max=5`);
        const fallbackData = await fallbackResponse.json();
        correctOption = fallbackData[0]?.word || getRandomWord(currentDifficulty);
      }

      let incorrectOptions = unrelatedWords.slice(0, 2);
      if (incorrectOptions.length < 2) {
        while (incorrectOptions.length < 2) {
          const randomWord = getRandomWord(currentDifficulty);
          if (randomWord !== word && randomWord !== correctOption && !incorrectOptions.includes(randomWord)) {
            incorrectOptions.push(randomWord);
          }
        }
      }

      const allOptions = shuffleArray([correctOption, ...incorrectOptions]);
      setOptions(allOptions);
      setCorrectAnswer(correctOption); // ✅ Store correct answer
    } catch (error) {
      console.error('Error fetching data:', error);
      const correctOption = getRandomWord(currentDifficulty);
      const incorrectOptions = [getRandomWord(currentDifficulty), getRandomWord(currentDifficulty)].filter(w => w !== correctOption);
      setOptions(shuffleArray([correctOption, ...incorrectOptions]));
      setCorrectAnswer(correctOption); // Fallback correct answer
    }
    setLoading(false);
  };

  const handleOptionClick = (option) => {
    if (option === correctAnswer) {
      setScore(score + 1);
      setMessage('✅ Correct! Moving to next word.');
      setMessageType('success');
      const nextDifficulty = getDifficultyLevel(score + 1);
      const nextWord = getRandomWord(nextDifficulty);
      setCurrentWord(nextWord);
    } else {
      setMessage(`❌ Incorrect! The related word was "${correctAnswer}".`);
      setMessageType('error');
      setScore(0);
      setCurrentWord(getRandomWord('easy'));
    }

    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 2000);
  };

  const resetGame = () => {
    setScore(0);
    setMessage('');
    setMessageType('');
    setCurrentDifficulty('easy');
    setCurrentWord(getRandomWord('easy'));
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  return (
    <div className="word-container">
      <SEO
  title="Word Association Game | Boldly English"
  description="Improve your English vocabulary with the Word Association Game. Choose related words, increase your score, and progress from easy to expert levels."
canonical="https://boldlyenglish.com/Wordgame"
/>
      <h1 className="word-h1">🔗 Word Association Game</h1>

      <div className="score-board">
        <div className="score-item highest-score">
          <div className="score-label">HIGHEST SCORE</div>
          <div className="score-value">{highestScore}</div>
        </div>
        <div className="score-item current-score">
          <div className="score-label">CURRENT SCORE</div>
          <div className="score-value">{score}</div>
        </div>
      </div>

      <div className="difficulty-indicator">
        <span className="difficulty-label">LEVEL:</span>
        <span className={`difficulty-value ${currentDifficulty}`}>{currentDifficulty.toUpperCase()}</span>
      </div>

      <div className="info-icon" onClick={toggleModal}>i</div>

      <div className="current-word">{currentWord}</div>

      {loading ? (
        <p className="word-p">Loading options...</p>
      ) : (
        <div className="word-options">
          {options.map((option, index) => (
            <button className="word-button" key={index} onClick={() => handleOptionClick(option)}>
              {option}
            </button>
          ))}
        </div>
      )}

      {message && <div className={`message ${messageType}`}>{message}</div>}

      <button className="word-button restart-button" onClick={resetGame}>
        Restart Game
      </button>

      <div className={`modal ${showModal ? 'show' : ''}`}>
        <div className="modal-content">
          <span className="close-modal" onClick={toggleModal}>&times;</span>
          <h2 className="modal-title">How to Play</h2>
          <ol className="how-to-list">
            <li>You'll be shown a word and three options below it.</li>
            <li>Select the word that you think is most <strong>related</strong> to the current word.</li>
            <li>If you choose correctly, your score increases by 1 and a new word is selected.</li>
            <li>If you choose incorrectly, your score resets to 0 and you return to easy words.</li>
            <li>As your score increases, the words get more difficult!</li>
            <li>Try to beat your highest score!</li>
          </ol>
          <p>The game uses word associations to test your vocabulary and understanding of semantic relationships.</p>
          <div className="footer">Word Association Game © 2025</div>
        </div>
      </div>
    </div>
  );
};

export default Wordgame;
